import { Common } from "@common/types";
import { ValidationError } from "./api";
import i18n from "./i18n";

export type ToastOptions = Common.JQuery.ToastOptions & {
  className?: string;
};

export type Toasts = {
  deleted: () => void;
  error: (error: string | Error) => void;
  info: (text: string, opts?: Partial<ToastOptions>) => void;
  show: (options: ToastOptions) => void;
  saved: () => void;
};

const DEFAULT_INFO_OPTIONS: Partial<Common.JQuery.ToastOptions> = {
  allowToastClose: false,
  position: "bottom-right",
  loader: false,
  hideAfter: 2000,
  stack: 1,
  textAlign: "left"
};

export const DEFAULT_ERROR_OPTIONS: Partial<Common.JQuery.ToastOptions> = {
  allowToastClose: true,
  position: "bottom-right",
  textAlign: "left",
  loader: false,
  icon: "error",
  hideAfter: false
};

const wrapText = (text: string) => `<div className="content">${text}</div>`;

const toast = (options: ToastOptions) => {
  window["$"].toast({
    ...options,
    beforeShow: options.className
      ? (args: [HTMLElement]) => {
          args[0].classList.add(...options.className!.split(" "));
        }
      : undefined
  });
};

const info = (text: string, opts?: Partial<ToastOptions>) =>
  toast({
    ...DEFAULT_INFO_OPTIONS,
    text: wrapText(text),
    stack: opts?.stack ? Number.MAX_SAFE_INTEGER : 1
  });

const toasts = {
  error: (error: string | Error, opts?: Partial<ToastOptions>) =>
    toast({
      ...DEFAULT_ERROR_OPTIONS,
      text: wrapText(error instanceof Error ? error.message : error)
    }),
  info,
  show: (options: ToastOptions) =>
    toast({
      ...options,
      text: wrapText(options.text)
    }),
  deleted: (opts?: Partial<ToastOptions>) => {
    info(i18n.deleted, opts);
  },
  saved: (opts?: Partial<ToastOptions>) => {
    info(i18n.saved, opts);
  },
  success: () => {
    info(i18n.success);
  },
  submitted: () => {
    info(i18n.submitted);
  }
};
export default toasts;

export const withDeletedToast = async <T>(action: () => Promise<T>, done?: () => Promise<void> | void): Promise<T> =>
  withToast(i18n.deleted, action, done);

export const withSavedToast = async <T>(action: () => Promise<T>, done?: () => Promise<void> | void): Promise<T> =>
  withToast(i18n.saved, action, done);

export const wrapToast =
  <T extends unknown[]>(message: string, action: (...args: T) => Promise<any | void>, done?: () => void) =>
  async (...args: T): Promise<any> => {
    try {
      const result = await action(...args);
      toasts.info(message);
      return result;
    } finally {
      done && done();
    }
  };

export const wrapDeletedToast = <T extends unknown[]>(action: (...args: T) => Promise<any | void>, done?: () => void) =>
  wrapToast(i18n.deleted, action, done);
export const wrapSavedToast = <T extends unknown[]>(action: (...args: T) => Promise<any | void>, done?: () => void) =>
  wrapToast(i18n.saved, action, done);

export const withToast = async <T>(
  message: string,
  action: () => Promise<T>,
  done?: () => Promise<void> | void
): Promise<T> => {
  try {
    const result = await action();
    toasts.info(message);
    return result;
  } catch (e) {
    !(e instanceof ValidationError) && toasts.error(e.message);
    throw e;
  } finally {
    const res = done && done();
    if (res instanceof Promise) {
      await res;
    }
  }
};
